import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { cn } from '../../utils/cn';
import type { AnimatedFillProps } from './AnimatedFill.types';
import {
  indeterminateLeft,
  isSheenOn,
  nextProgressTarget,
  omegaFor,
  PROGRESS_INDETERMINATE_EASING_CSS,
  PROGRESS_MOTION,
  PROGRESS_SHEEN_PERIOD_MS,
  sheenLeft,
  smoothingFor,
  springStep,
} from './progress-motion';

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const query = window.matchMedia(REDUCED_QUERY);
  query.addEventListener?.('change', onChange);
  return () => query.removeEventListener?.('change', onChange);
}

const readReducedMotion = () =>
  typeof window !== 'undefined' && (window.matchMedia?.(REDUCED_QUERY).matches ?? false);

/** Fill / sheen transforms, in units of each element's OWN width. */
const fillTransform = (x: number) => `translateX(${(x - 1) * 100}%)`;
const sheenTransform = (left: number, x: number) =>
  // The sheen lives inside the translated fill, so shift it back into track
  // coordinates (+ (1 − x)); its own width is `band` of the track.
  `translateX(${((left + 1 - x) / PROGRESS_MOTION.sheen.band) * 100}%)`;

/**
 * Linear fill (web). A full-width fill slides with `translateX` (round cap
 * kept, compositor-only); the spring from `progress-motion.ts` is stepped on
 * rAF and written straight to the node, so React never re-renders per frame.
 * The loop sleeps once the fill settles and no sheen is running.
 */
export function AnimatedFill({
  fraction,
  className,
  live,
  indeterminate,
  sheen,
}: AnimatedFillProps) {
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    readReducedMotion,
    () => false,
  );
  const fillRef = useRef<HTMLDivElement>(null);
  const sheenRef = useRef<HTMLDivElement>(null);
  const segmentRef = useRef<HTMLDivElement>(null);

  // Mount at the value (no intro glide), like the CSS transition it replaced.
  // `initialX` only seeds the first paint's inline transform — after mount the
  // frame loop owns `transform`, and React never rewrites it (same value).
  const [initialX] = useState(() => (indeterminate ? 0 : fraction));
  const motion = useRef({
    x: initialX,
    v: 0,
    target: initialX,
    raf: 0,
    last: 0,
    sheenOn: false,
    sheenStart: 0,
  });

  // The segment stays mounted after the first indeterminate phase so the
  // hand-off can fade it out; plain bars never mount it (or the sheen).
  const [segmentMounted, setSegmentMounted] = useState(indeterminate);
  if (indeterminate && !segmentMounted) setSegmentMounted(true);
  const sheenWanted = (sheen ?? live) && !reducedMotion;
  const sheenOn = isSheenOn({ sheen, live, indeterminate, fraction, reducedMotion });

  // Fill + sheen: retarget the spring and (re)start the frame loop.
  useEffect(() => {
    const m = motion.current;
    const fill = fillRef.current;
    if (!fill) return;
    const { target, snap } = indeterminate
      ? { target: 0, snap: true }
      : nextProgressTarget(m.target, fraction, live);
    m.target = target;
    if (snap || reducedMotion) {
      m.x = target;
      m.v = 0;
    }
    if (sheenOn && !m.sheenOn) m.sheenStart = performance.now();
    m.sheenOn = sheenOn;

    const paint = (now: number) => {
      fill.style.transform = fillTransform(m.x);
      const sheenNode = sheenRef.current;
      if (sheenNode && m.sheenOn) {
        const phase = (now - m.sheenStart) / PROGRESS_SHEEN_PERIOD_MS;
        sheenNode.style.transform = sheenTransform(sheenLeft(phase, m.x), m.x);
      }
    };

    const tick = (now: number) => {
      const dt = m.last ? (now - m.last) / 1000 : 0;
      m.last = now;
      const next = springStep(m, m.target, omegaFor(smoothingFor(m.target, live)), dt);
      m.x = live ? Math.max(m.x, next.x) : next.x;
      m.v = next.v;
      paint(now);
      m.raf = next.settled && !m.sheenOn ? 0 : requestAnimationFrame(tick);
    };

    cancelAnimationFrame(m.raf);
    m.last = 0;
    paint(performance.now());
    m.raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(m.raf);
      m.raf = 0;
    };
  }, [fraction, live, indeterminate, reducedMotion, sheenOn]);

  // Indeterminate: one calm segment on a compositor-driven WAAPI loop.
  useEffect(() => {
    const segment = segmentRef.current;
    if (
      !indeterminate ||
      reducedMotion ||
      !segment ||
      typeof segment.animate !== 'function'
    ) {
      return;
    }
    const { periodMs, segment: width } = PROGRESS_MOTION.indeterminate;
    const toOwn = (left: number) => `translateX(${(left / width) * 100}%)`;
    const animation = segment.animate(
      [
        { transform: toOwn(indeterminateLeft(0)) },
        { transform: toOwn(indeterminateLeft(1)) },
      ],
      {
        duration: periodMs,
        iterations: Infinity,
        easing: PROGRESS_INDETERMINATE_EASING_CSS,
      },
    );
    return () => animation.cancel();
  }, [indeterminate, reducedMotion, segmentMounted]);

  const { handoffMs, indeterminate: ind, sheen: sh } = PROGRESS_MOTION;
  const fade = (ms: number) => ({
    transitionProperty: 'opacity',
    transitionDuration: `${ms}ms`,
  });

  return (
    <>
      <div
        ref={fillRef}
        aria-hidden
        className={cn('absolute inset-y-0 left-0 w-full overflow-hidden', className)}
        style={{
          transform: fillTransform(initialX),
          opacity: indeterminate ? 0 : 1,
          ...fade(handoffMs),
        }}>
        {sheenWanted ? (
          <div
            ref={sheenRef}
            className="absolute inset-y-0 left-0"
            style={{
              width: `${sh.band * 100}%`,
              transform: sheenTransform(initialX, initialX),
              // The highlight is the stable on-primary role (light in both
              // themes) — a token, never a hex; the gradient only fades its alpha.
              backgroundImage: `linear-gradient(90deg, rgba(var(--fg-on-primary-rgb), 0), rgba(var(--fg-on-primary-rgb), ${sh.peakOpacity}), rgba(var(--fg-on-primary-rgb), 0))`,
              opacity: sheenOn ? 1 : 0,
              ...fade(sh.fadeMs),
            }}
          />
        ) : null}
      </div>
      {segmentMounted ? (
        <div
          ref={segmentRef}
          aria-hidden
          className={cn('absolute inset-y-0 left-0', className)}
          style={{
            width: reducedMotion ? '100%' : `${ind.segment * 100}%`,
            transform: reducedMotion
              ? undefined
              : `translateX(${(indeterminateLeft(0) / ind.segment) * 100}%)`,
            opacity: indeterminate ? (reducedMotion ? ind.reducedOpacity : 1) : 0,
            ...fade(handoffMs),
          }}
        />
      ) : null}
    </>
  );
}
